// studentSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { setError } from './errorSlice';

const initialState = {
  list: [],
  terms: [],
  newStudents: [],
  status: 'idle',          // for fetchStudentsData/terms
  newStatus: 'idle',       // for fetchNewStudents
  error: null,
  newError: null,
};

// Fetch students in batch-section
export const fetchStudentsData = createAsyncThunk(
  'student/fetchStudentsData',
  async ({ termId, batchId, sectionId }, { rejectWithValue, dispatch }) => {
    try {
      const storedUser = localStorage.getItem('userInfo');
      if (!storedUser) {
        dispatch(setError('Session expired. Please log in again.'));
        return rejectWithValue('Session expired. Please log in again.');
      }
      const { token } = JSON.parse(storedUser);
      if (!token) {
        dispatch(setError('Session expired. Please log in again.'));
        return rejectWithValue('Session expired. Please log in again.');
      }
      const response = await axios.get(
        `${API_BASE_URL}/api/Student/student-in-batch-section/${termId}/${batchId}/${sectionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (err) {
      console.error('Fetch Students Data Error:', err);
      return rejectWithValue(err.response?.data || err.message || 'Failed to fetch students');
    }
  }
);

// Fetch terms
export const fetchTerms = createAsyncThunk(
  'student/fetchTerms',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const storedUser = localStorage.getItem('userInfo');
      if (!storedUser) {
        dispatch(setError('Session expired. Please log in again.'));
        return rejectWithValue('Session expired. Please log in again.');
      }
      const { token } = JSON.parse(storedUser);
      if (!token) {
        dispatch(setError('Session expired. Please log in again.'));
        return rejectWithValue('Session expired. Please log in again.');
      }
      const response = await axios.get(
        `${API_BASE_URL}/api/Term`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (err) {
      console.error('Fetch Terms Error:', err);
      return rejectWithValue(err.response?.data || err.message || 'Failed to fetch terms');
    }
  }
);

// Fetch newly registered students
export const fetchNewStudents = createAsyncThunk(
  'student/fetchNewStudents',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const storedUser = localStorage.getItem('userInfo');
      if (!storedUser) {
        dispatch(setError('Session expired. Please log in again.'));
        return rejectWithValue('Session expired. Please log in again.');
      }
      const { token } = JSON.parse(storedUser);
      if (!token) {
        dispatch(setError('Session expired. Please log in again.'));
        return rejectWithValue('Session expired. Please log in again.');
      }
      const response = await axios.get(
        `${API_BASE_URL}/api/Student/newly-registered`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (err) {
      console.error('Fetch New Students Error:', err);
      return rejectWithValue(err.response?.data || err.message || 'Failed to fetch new students');
    }
  }
);

const studentIDCardSlice = createSlice({
  name: 'studentIDCard',
  initialState,
  reducers: {
    resetStudentState(state) {
      state.list = [];
      state.terms = [];
      state.newStudents = [];
      state.status = 'idle';
      state.newStatus = 'idle';
      state.error = null;
      state.newError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchStudentsData
      .addCase(fetchStudentsData.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchStudentsData.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.list = action.payload;
      })
      .addCase(fetchStudentsData.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || action.error.message;
      })

      // fetchTerms
      .addCase(fetchTerms.fulfilled, (state, action) => {
        state.terms = action.payload;
      })
      .addCase(fetchTerms.rejected, (state, action) => {
        state.error = action.payload || action.error.message;
      })

      // fetchNewStudents
      .addCase(fetchNewStudents.pending, (state) => {
        state.newStatus = 'loading';
        state.newError = null;
      })
      .addCase(fetchNewStudents.fulfilled, (state, action) => {
        state.newStatus = 'succeeded';
        state.newStudents = action.payload;
      })
      .addCase(fetchNewStudents.rejected, (state, action) => {
        state.newStatus = 'failed';
        state.newError = action.payload || action.error.message;
      });
  },
});

export const { resetStudentState } = studentIDCardSlice.actions;
export default studentIDCardSlice.reducer;
